import { useEffect, useState } from "react";
import { Save, Info } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";

const EMPTY = { api_name: "", api_id: "", base_url: "", notes: "", updated_at: null };

export default function GatewayApiCard({ disabled }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (disabled) { setLoading(false); return; }
    (async () => {
      try {
        const r = await api.get("/gateway/config");
        setForm({ ...EMPTY, ...r.data });
      } catch (e) {
        toast.error(apiErrorMessage(e, "No se pudo cargar la configuración"));
      } finally {
        setLoading(false);
      }
    })();
  }, [disabled]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/gateway/config", {
        api_name: form.api_name,
        api_id: form.api_id,
        base_url: form.base_url,
        notes: form.notes,
      });
      setForm({ ...EMPTY, ...r.data });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 p-6 space-y-5 max-w-2xl">
      <div className="text-sm text-zinc-400 flex items-start gap-2">
        <Info className="h-3.5 w-3.5 mt-0.5 text-zinc-500 shrink-0" />
        <span>
          Define la identidad pública de tu Gateway. La <code>Base URL</code> es
          informativa: aún no existe un endpoint público funcional.
        </span>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-zinc-400">Nombre de la API</Label>
        <Input
          data-testid={TID.gateway.apiName}
          value={form.api_name}
          onChange={(e) => setForm({ ...form, api_name: e.target.value })}
          disabled={disabled || loading}
          placeholder="KRAI API"
          className="bg-zinc-900 border-zinc-800"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-zinc-400">Identificador interno</Label>
        <Input
          data-testid={TID.gateway.apiId}
          value={form.api_id}
          onChange={(e) => setForm({ ...form, api_id: e.target.value.toLowerCase() })}
          disabled={disabled || loading}
          placeholder="krai-api"
          className="bg-zinc-900 border-zinc-800 font-mono text-sm"
        />
        <div className="text-[10px] text-zinc-500">Solo minúsculas, dígitos y guiones (máx 64).</div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-zinc-400">Base URL (futura)</Label>
        <Input
          data-testid={TID.gateway.apiBaseUrl}
          value={form.base_url}
          onChange={(e) => setForm({ ...form, base_url: e.target.value })}
          disabled={disabled || loading}
          placeholder="https://api.midominio.com"
          className="bg-zinc-900 border-zinc-800 font-mono text-sm"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-zinc-400">Notas</Label>
        <Textarea
          data-testid={TID.gateway.apiNotes}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          disabled={disabled || loading}
          rows={3}
          className="bg-zinc-900 border-zinc-800 text-sm resize-none"
        />
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
        <div className="text-[10px] text-zinc-500 font-mono">
          {form.updated_at
            ? `actualizado: ${new Date(form.updated_at).toLocaleString()}`
            : "sin guardar"}
        </div>
        <Button
          data-testid={TID.gateway.apiSave}
          onClick={save}
          disabled={disabled || loading || saving}
          size="sm"
          className="gap-2 bg-white text-black hover:bg-zinc-200"
        >
          <Save className="h-3.5 w-3.5" />
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      <div className="rounded-md border border-zinc-800 bg-black/30 p-3 text-[11px] text-zinc-500 font-mono">
        <div className="text-zinc-400 mb-1">Estado</div>
        ⚪ Pendiente de integración con KRAI API funcional
      </div>
    </div>
  );
}
